import React, {useCallback} from 'react'

import {
  getAnonymousId,
  isSyntheticTestAnonymousId
} from '@vio/core/src/_web/anonymousId'
import {useIsGDPRCountry} from '@vio/core/src/_web/utils/cookies/hooks/cookieComplianceGeolocation'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/hooks/useDispatchTeamEvent'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {TrackEventPayload} from '@vio/core/src/analytics/types/Events'

import {CookieBannerBackground} from './CookieBannerBackground'
import {CookieConsentBannerStyles} from './styles'
import {useOsanoCookieConsent} from './useOsanoCookieConsent'

export interface CookieConsentBannerProps {
  /**
   * Callback invoked when a user accepts the cookies, either in the
   * banner or the preference center
   */
  onAccept?: (acceptedByUser: boolean) => void
  trackEvent: (payload: TrackEventPayload) => void
}

export const shouldCookieBannerBeShown = (): boolean =>
  window.Osano?.cm?.dialogOpen ?? false

const PAYMENT_PAGE_PATH = '/checkout/'
export const CookieConsentBanner = ({
  onAccept,
  trackEvent
}: CookieConsentBannerProps) => {
  const isPaymentPage = window.location.pathname.startsWith(PAYMENT_PAGE_PATH)
  const isGDPRCountry = useIsGDPRCountry()
  const bannerModAllowed = !!isGDPRCountry && !isPaymentPage
  const {backgroundVisible} = useOsanoCookieConsent({
    onAccept,
    trackEvent,
    withBannerBackground: bannerModAllowed
  })

  return (
    <>
      {backgroundVisible && <CookieBannerBackground />}
      <CookieConsentBannerStyles hideCloseButton={bannerModAllowed} />
    </>
  )
}

export const CookieConsentBannerWithStore = (
  props: Omit<CookieConsentBannerProps, 'trackEvent'>
) => {
  const dispatchTeamEvent = useDispatchTeamEvent()
  const trackEventCallback = useCallback(
    (payload: TrackEventPayload) => dispatchTeamEvent(trackEvent(payload)),
    [dispatchTeamEvent]
  )
  const anonymousId = getAnonymousId() as string
  const isSyntheticTest = isSyntheticTestAnonymousId(anonymousId)

  if (isSyntheticTest) return null

  return <CookieConsentBanner {...props} trackEvent={trackEventCallback} />
}
